import { useCallback, useEffect, useRef, useState } from 'react';

import { api, ApiClientError } from '../../services/api';
import type { AccountSummary } from '../../types';

type Props = {
  accounts: AccountSummary[];
  onReload: () => void;
};

const WhatsAppAccounts = ({ accounts, onReload }: Props) => {
  const [showCreate, setShowCreate] = useState(false);
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [qrForAccount, setQrForAccount] = useState<string | null>(null);
  const [qrAccountId, setQrAccountId] = useState<string | null>(null);
  const [actionAccount, setActionAccount] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, { ok: boolean; message: string } | null>>({});
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const accountsRef = useRef<AccountSummary[]>(accounts);
  const onReloadRef = useRef(onReload);

  // Keep refs in sync so the polling callback always sees the latest data
  // without restarting the interval on every accounts update.
  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);
  useEffect(() => {
    onReloadRef.current = onReload;
  }, [onReload]);

  const stopQrPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopQrPoll(), [stopQrPoll]);

  // Start polling QR for any account that is currently in qr_required state.
  // This makes the QR visible automatically on page load without requiring
  // the user to click "Verbinden" first.
  const ensureQrPolling = useCallback(() => {
    if (pollRef.current) return; // already polling
    const qrAccount = accountsRef.current.find(
      (a) => a.status.state === 'qr_required' || a.status.state === 'connecting',
    );
    if (!qrAccount) return;
    const accountId = qrAccount.id;
    setQrAccountId(accountId);
    pollRef.current = setInterval(async () => {
      const managed = accountsRef.current.find((a) => a.id === accountId);
      if (!managed) {
        stopQrPoll();
        setQrForAccount(null);
        setQrAccountId(null);
        return;
      }
      // Stop polling once connected
      if (managed.status.state === 'ready' || managed.status.state === 'authenticated') {
        setQrForAccount(null);
        setQrAccountId(null);
        stopQrPoll();
        void onReloadRef.current();
        return;
      }
      if (managed.status.state === 'disconnected' || managed.status.state === 'error') {
        setQrForAccount(null);
        setQrAccountId(null);
        stopQrPoll();
        return;
      }
      if (managed.status.state === 'qr_required') {
        try {
          const result = await api.getAccountQr(accountId);
          setQrForAccount(result.qr);
        } catch (reason) {
          // 409 QR_NOT_READY is expected while the connector is still generating
          // the QR — keep polling silently. Other errors should be logged.
          if (reason instanceof ApiClientError && reason.code === 'QR_NOT_READY') return;
          console.error('[whatsapp-accounts poll] getAccountQr failed', reason);
        }
      } else {
        // connecting / reconnecting — refresh status from server so we
        // eventually see the qr_required transition.
        try {
          await onReloadRef.current();
        } catch (reason) {
          console.error('[whatsapp-accounts poll] reload failed', reason);
        }
      }
    }, 2000);
  }, [stopQrPoll]);

  // On mount and whenever accounts change, ensure we are polling if any
  // account is waiting for a QR scan.
  useEffect(() => {
    ensureQrPolling();
  }, [accounts, ensureQrPolling]);

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!label.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const account = await api.createAccount({ label: label.trim(), provider: 'local_qr' });
      setLabel('');
      setShowCreate(false);
      await onReload();
      await handleConnect(account.id);
    } catch (reason) {
      const message = reason instanceof ApiClientError ? reason.message : 'Account kon niet worden aangemaakt.';
      setError(message);
      console.error('[whatsapp-accounts] create failed', reason);
    } finally {
      setCreating(false);
    }
  };

  const handleConnect = async (accountId: string) => {
    setActionAccount(accountId);
    setError(null);
    setNotice(null);
    setQrForAccount(null);
    setQrAccountId(accountId);
    try {
      const status = await api.connectAccount(accountId);
      // If connect returned a status that already has a QR, use it
      if (status.state === 'qr_required' && status.qrDataUrl) {
        setQrForAccount(status.qrDataUrl);
      }
      await onReload();
    } catch (reason) {
      const message = reason instanceof ApiClientError ? reason.message : 'Verbinden mislukt.';
      setError(message);
      console.error('[whatsapp-accounts] connect failed', reason);
    } finally {
      setActionAccount(null);
    }
  };

  const handleDisconnect = async (accountId: string) => {
    setActionAccount(accountId);
    setError(null);
    try {
      await api.disconnectAccount(accountId);
      setQrForAccount(null);
      setQrAccountId(null);
      stopQrPoll();
      await onReload();
    } catch (reason) {
      const message = reason instanceof ApiClientError ? reason.message : 'Verbreken mislukt.';
      setError(message);
      console.error('[whatsapp-accounts] disconnect failed', reason);
    } finally {
      setActionAccount(null);
    }
  };

  const handleActivate = async (accountId: string) => {
    setActionAccount(accountId);
    setError(null);
    try {
      await api.activateAccount(accountId);
      await onReload();
    } catch (reason) {
      const message = reason instanceof ApiClientError ? reason.message : 'Activeren mislukt.';
      setError(message);
      console.error('[whatsapp-accounts] activate failed', reason);
    } finally {
      setActionAccount(null);
    }
  };

  const handleRemove = async (accountId: string) => {
    if (!confirm('Weet je zeker dat je dit account wilt verwijderen?')) return;
    setActionAccount(accountId);
    setError(null);
    try {
      await api.removeAccount(accountId);
      setQrForAccount(null);
      setQrAccountId(null);
      await onReload();
    } catch (reason) {
      const message = reason instanceof ApiClientError ? reason.message : 'Verwijderen mislukt.';
      setError(message);
      console.error('[whatsapp-accounts] remove failed', reason);
    } finally {
      setActionAccount(null);
    }
  };

  const handleTest = async (accountId: string) => {
    setTestingId(accountId);
    setNotice(null);
    setTestResult((prev) => ({ ...prev, [accountId]: null }));
    try {
      // Refresh status as the "test": if connector is ready, we report success.
      await onReload();
      const acc = accountsRef.current.find((a) => a.id === accountId);
      const state = acc?.status.state ?? 'unknown';
      if (state === 'ready' || state === 'authenticated') {
        setTestResult((prev) => ({
          ...prev,
          [accountId]: { ok: true, message: `Verbinding OK (${state})` },
        }));
      } else {
        setTestResult((prev) => ({
          ...prev,
          [accountId]: { ok: false, message: `Niet verbonden — huidige status: ${state}` },
        }));
      }
    } catch (reason) {
      const message = reason instanceof ApiClientError ? reason.message : 'Test mislukt.';
      setTestResult((prev) => ({ ...prev, [accountId]: { ok: false, message } }));
      console.error('[whatsapp-accounts] test failed', reason);
    } finally {
      setTestingId(null);
    }
  };

  return (
    <section className="whatsapp-accounts" aria-label="WhatsApp accounts">
      <div className="whatsapp-accounts-header">
        <div>
          <p className="eyebrow">WhatsApp</p>
          <h3>Gekoppelde nummers</h3>
        </div>
        <button type="button" className="button primary small" onClick={() => setShowCreate((v) => !v)}>
          + Nummer toevoegen
        </button>
      </div>

      {error && <p className="form-error">{error}</p>}
      {notice && <p className="notice" role="status">{notice}</p>}

      {showCreate && (
        <form className="whatsapp-account-create" onSubmit={handleCreate}>
          <label>
            Naam voor dit nummer
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="bijv. Werk, Persoonlijk, Klantenservice"
              maxLength={100}
              required
            />
          </label>
          <button type="submit" className="button primary small" disabled={creating}>
            {creating ? 'Aanmaken...' : 'Aanmaken en koppelen'}
          </button>
        </form>
      )}

      {qrForAccount && qrAccountId && (
        <div className="qr-poll-display">
          <p>Scan de QR-code met WhatsApp op je telefoon:</p>
          <img src={qrForAccount} alt="WhatsApp QR code" className="qr-image" />
          <p className="qr-hint">WhatsApp → Instellingen → Gekoppelde apparaten → Apparaat koppelen</p>
          <p className="muted small">De QR wordt automatisch vernieuwd. Niet afsluiten tot de scan klaar is.</p>
        </div>
      )}

      <div className="whatsapp-account-list">
        {accounts.length === 0 && !showCreate && (
          <p className="muted">Geen WhatsApp-nummers gekoppeld. Klik op "+ Nummer toevoegen" om te beginnen.</p>
        )}
        {accounts.map((account) => (
          <div key={account.id} className={`whatsapp-account-card ${account.isActive ? 'active' : ''}`}>
            <div className="whatsapp-account-info">
              <div className="whatsapp-account-label">
                <span className="account-name">{account.label}</span>
                {account.isActive && <span className="badge active">Actief</span>}
              </div>
              <span className={`account-status status-${account.status.state}`}>
                {account.status.state === 'ready' ? 'Verbonden' :
                 account.status.state === 'qr_required' ? 'Wacht op QR-scan' :
                 account.status.state === 'connecting' ? 'Verbinden...' :
                 account.status.state === 'disconnected' ? 'Niet verbonden' :
                 account.status.state === 'authenticated' ? 'Geauthenticeerd' :
                 account.status.state === 'reconnecting' ? 'Opnieuw verbinden...' :
                 account.status.state === 'degraded' ? 'Verstoord' :
                 'Fout'}
              </span>
              {account.status.reason && (
                <p className="muted small account-reason">{account.status.reason}</p>
              )}
              {testResult[account.id] && (
                <p className={`notice small ${testResult[account.id]?.ok ? 'success' : 'error'}`}>
                  {testResult[account.id]?.ok ? '✅' : '❌'} {testResult[account.id]?.message}
                </p>
              )}
            </div>
            <div className="whatsapp-account-actions">
              <button
                type="button"
                className="button small"
                onClick={() => void handleTest(account.id)}
                disabled={testingId === account.id}
                title="Test huidige verbindingstatus"
              >
                {testingId === account.id ? 'Testen…' : 'Test'}
              </button>
              {account.status.state === 'ready' || account.status.state === 'authenticated' ? (
                <button
                  type="button"
                  className="button small"
                  onClick={() => void handleDisconnect(account.id)}
                  disabled={actionAccount === account.id}
                >
                  Verbreken
                </button>
              ) : (
                <button
                  type="button"
                  className="button primary small"
                  onClick={() => void handleConnect(account.id)}
                  disabled={actionAccount === account.id}
                >
                  {actionAccount === account.id ? '...' : 'Verbinden'}
                </button>
              )}
              {!account.isActive && (account.status.state === 'ready' || account.status.state === 'authenticated') && (
                <button
                  type="button"
                  className="button small"
                  onClick={() => void handleActivate(account.id)}
                  disabled={actionAccount === account.id}
                >
                  Activeren
                </button>
              )}
              <button
                type="button"
                className="button small danger"
                onClick={() => void handleRemove(account.id)}
                disabled={actionAccount === account.id}
              >
                Verwijderen
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default WhatsAppAccounts;
